// @flow
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import formatNumber from 'utils/formatNumber';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';

import getSubscriptionBackground from 'utils/getSubscriptionBackground';
import getSubscriptionStatus from 'utils/getSubscriptionStatus';
import getFrequency from 'utils/getFrequency';
import getBillingFrequency from 'utils/getBillingFrequency';
import {
  Card,
  Table,
  Row,
  Col,
  Tooltip,
  Button,
} from 'antd';

import {
  axiosAuthInstance2,
  API_END_POINTS,
} from 'api';
import { NOTIFICATION } from 'store/actionTypes';

import {
  SpForm,
  SpText,
  SpH5,
  SpStatusTag,
} from 'components/DesignKit';

import Loading from 'components/Loading';
import {
  planv2DetailsActions,
  notificationActions,
} from 'store/actions';
import { PAGES } from 'appconstants';

type Props = {
  fetchPlanV2Details: Function,
  fetchPlanV2Subscription: Function,
  plansDetails: Object,
  loading: boolean,
  selectedAccount: Object,
  history: {
    push: Function,
  },
  match: {
    params: Object,
  },
  subscription: Array<Object>,
  totalElements: number,
  setNotification: Function,
}

const PlanDetails = (props: Props) => {
  const location = useLocation();
  const {
    fetchPlanV2Details,
    fetchPlanV2Subscription,
    plansDetails,
    loading,
    history,
    subscription,
    totalElements,
    selectedAccount,
    setNotification,
  } = props;
  const { gid } = props.match.params;
  const [pagination, setPagination] = useState({});
  const gidPath = gid.split('-')[1];
  const { prefix } = selectedAccount && selectedAccount.currency;
  const currencyType = selectedAccount && selectedAccount.currency;

  const getData = (query) => {
    fetchPlanV2Details(gid);
    const parsed = queryString.parse(query);
    const currentPage = parsed.pageNum || 1;
    const sortBy = parsed.sortBy || 'createdAt';
    const direction = parsed.direction || 'DESC';
    const sortParams = `&sortBy=${sortBy}&direction=${direction}`;
    fetchPlanV2Subscription({ currentPage, sortParams, gid });
  };

  const selectedRecord = (record) => {
    const searchParams = record.gid;
    history.push({
      pathname: `${PAGES.SUBSCRIPTION}/${searchParams}`,
      state: location.search,
    });
  };

  /**
   * Get data on page load
   */
  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (!gidPath) {
      history.push(PAGES.PLAN);
    }
  }, []);

  /**
   * Get data when selected account changes
   */
  useEffect(() => {
    getData(location.search);
  }, [location, selectedAccount]);

  useEffect(() => {
    const parsed = queryString.parse(location.search);
    const currentPage = parsed.pageNum || 1;
    setPagination(existingElements => ({
      ...existingElements,
      total: totalElements,
      current: parseInt(currentPage, 10),
      showTotal: total => `Total ${total} items`,
    }));
  }, [subscription]);

  const handleTableChange = (page, sorter, filters) => {
    const tempPage = page.current;
    let tempParams = '';
    if (filters.order === 'ascend') {
      tempParams = `&sortBy=${filters.field}&direction=ASC`;
    } else if (filters.order === 'descend') {
      tempParams = `&sortBy=${filters.field}&direction=DESC`;
    } else {
      tempParams = '';
    }
    history.push({
      pathname: `/subscriptions/plans/${gid}`,
      search: `?pageNum=${tempPage}${tempParams}`,
    });
  };

  const deactivatePlan = async (planType) => {
    try {
      await axiosAuthInstance2.patch(`${API_END_POINTS.PLAN}/${plansDetails?.gid}/${planType}`, {});
      setNotification({
        type: NOTIFICATION.SUCCESS,
        payload: `Plan ${planType}d successfully`,
      });
      getData(location.search);
    } catch (e) {
      setNotification({
        type: NOTIFICATION.ERROR,
        payload: e.response.data && e.response.data.message,
      });
    }
  };

  const columns = [
    {
      title: 'AMOUNT',
      dataIndex: 'amount',
      width: '13%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (amount) => {
        const total = (amount / 100).toFixed(2);
        return (
          <Tooltip placement="top" title="Click here to view details">
            <span>
              {`${prefix}${formatNumber(total, currencyType)}`}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'NAME',
      dataIndex: 'customer.name',
      width: '14%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (name) => (
        <Tooltip placement="top" title="Click here to view details">
          <span style={{ cursor: 'pointer' }}>
            {name || <>&#8211;</>}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'EMAIL',
      dataIndex: 'customer.email',
      width: '14%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (email) => (
        <Tooltip placement="top" title="Click here to view details">
          <span style={{ cursor: 'pointer' }}>
            {email || <>&#8211;</>}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'PHONE',
      dataIndex: 'customer.phoneNumber',
      width: '14%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (phoneNumber) => (
        <Tooltip placement="top" title="Click here to view details">
          <span style={{ cursor: 'pointer' }}>
            {phoneNumber || <>&#8211;</>}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'PLAN',
      dataIndex: 'plan2.name',
      width: '12%',
      align: 'left',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (plan) => (
        <Tooltip placement="top" title="Click here to view details">
          <span style={{ cursor: 'pointer' }}>
            {plan || <>&#8211;</>}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      width: '12%',
      align: 'left',
      sorter: false,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (status) => (
        <Tooltip placement="top" title="Click here to view details">
          <SpStatusTag style={{ backgroundColor: getSubscriptionBackground(status) }}>
            {getSubscriptionStatus(status)}
          </SpStatusTag>
        </Tooltip>
      ),
    },
    {
      title: 'CREATED ON',
      dataIndex: 'createdAt',
      width: '15%',
      align: 'left',
      sorter: true,
      defaultSortOrder: 'descend',
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (createdAt) => {
        if (createdAt && selectedAccount && selectedAccount.timezone) {
          const tzDate = moment
            .utc(createdAt)
            .tz(selectedAccount.timezone)
            .format('MMM DD, YYYY, hh:mm a');
          return (<span>{tzDate}</span>);
        }
        return (
          <>&#8211;</>
        );
      },
    },
  ];

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      {
        plansDetails?.gid && (
          <>
            <Card className="mb-3">
              <Row type="flex" justify="space-between" className="my-2 mb-3">
                <Col>
                  {plansDetails.name
                    ? <SpText className="text-uppercase mr-4" fontSize="20px">{plansDetails.name}</SpText>
                    : ''}
                </Col>
                <Col>
                  {plansDetails.status === 'ACTIVE'
                    ? (
                      <Button
                        className="ml-2"
                        type="danger"
                        shape="round"
                        onClick={() => deactivatePlan('deactivate')}
                      >
                        Deactivate
                      </Button>
                    )
                    : (
                      <Button
                        className="ml-2"
                        type="primary"
                        shape="round"
                        onClick={() => deactivatePlan('reactivate')}
                      >
                        Activate
                      </Button>
                    )}
                </Col>
              </Row>
              <Row type="flex" className="my-2 mb-3">
                <Col span={14}>
                  <SpForm>
                    <Row type="flex" justify="start" align="middle">
                      <Col span={10}>
                        <span>Billing Frequency</span>
                      </Col>
                      <Col span={12}>
                        {plansDetails.billingPeriod
                          ? <SpH5>{`Every ${plansDetails.billingPeriod} ${getFrequency(plansDetails.billingFrequency)}`}</SpH5>
                          : (
                            <>
                              {
                          plansDetails.billingFrequency && !plansDetails.billingPeriod ? (
                            <SpH5>
                              {getBillingFrequency(plansDetails.billingFrequency)}
                            </SpH5>
                          ) : (
                            <>&#8211;</>
                          )
                        }
                            </>
                          )}
                      </Col>
                    </Row>
                  </SpForm>
                  <SpForm>
                    <Row type="flex" justify="start" align="middle">
                      <Col span={10}>
                        <span>Description</span>
                      </Col>
                      <Col span={12}>
                        {plansDetails.description
                          ? <SpH5>{plansDetails.description}</SpH5>
                          : <>&#8211;</>}
                      </Col>
                    </Row>
                  </SpForm>
                  <SpForm>
                    <Row type="flex" justify="start" align="middle">
                      <Col span={10}>
                        <span>Internal Note</span>
                      </Col>
                      <Col span={12}>
                        {plansDetails.note
                          ? <SpH5>{plansDetails.note}</SpH5>
                          : <>&#8211;</>}
                      </Col>
                    </Row>
                  </SpForm>
                  <SpForm>
                    <Row type="flex" justify="start" align="middle">
                      <Col span={10}>
                        <span>Delay Start Date</span>
                      </Col>
                      <Col span={12}>
                        {plansDetails.delayStartDays
                          ? <SpH5>{plansDetails.delayStartDays}</SpH5>
                          : <>&#8211;</>}
                      </Col>
                    </Row>
                  </SpForm>
                  <SpForm>
                    <Row type="flex" justify="start" align="middle">
                      <Col span={10}>
                        <span>Free Trial Days</span>
                      </Col>
                      <Col span={12}>
                        {plansDetails.freeTrialDays
                          ? <SpH5>{plansDetails.freeTrialDays}</SpH5>
                          : <>&#8211;</>}
                      </Col>
                    </Row>
                  </SpForm>
                  <SpForm>
                    <Row type="flex" justify="start" align="middle">
                      <Col span={10}>
                        <span>Overdue Fees</span>
                      </Col>
                      <Col span={12}>
                        {plansDetails.description
                          ? <SpH5>{plansDetails.overDueFees / 100}{plansDetails?.overDueFeeType === 'PERCENT' ? '%' : prefix}</SpH5>
                          : <>&#8211;</>}
                      </Col>
                    </Row>
                  </SpForm>
                  <SpForm>
                    <Row type="flex" justify="start" align="middle">
                      <Col span={10}>
                        <span>Saved Payment Methods Limit</span>
                      </Col>
                      <Col span={12}>
                        {plansDetails.savedPaymentsLimit
                          ? <SpH5>{plansDetails.savedPaymentsLimit}</SpH5>
                          : <>&#8211;</>}
                      </Col>
                    </Row>
                  </SpForm>
                  <SpForm>
                    <Row type="flex" justify="start" align="middle">
                      <Col span={10}>
                        <span>Allow Manual Payments</span>
                      </Col>
                      <Col span={12}>
                        <SpH5>{plansDetails?.allowManualPayments ? 'YES' : 'NO'}</SpH5>
                      </Col>
                    </Row>
                  </SpForm>
                  <SpForm>
                    <Row type="flex" justify="start" align="middle">
                      <Col span={10}>
                        <span>Days To Next Charge Attempt After Fail</span>
                      </Col>
                      <Col span={12}>
                        {plansDetails.nextChargeAttemptDelayDays
                          ? <SpH5>{plansDetails.nextChargeAttemptDelayDays}</SpH5>
                          : <>&#8211;</>}
                      </Col>
                    </Row>
                  </SpForm>
                  <SpForm>
                    <Row type="flex" justify="start" align="middle">
                      <Col span={10}>
                        <span>Notify</span>
                      </Col>
                      <Col span={12}>
                        {plansDetails.notificationType
                          ? <SpH5>{plansDetails.notificationType === 'ALL' ? 'EMAIL and SMS' : plansDetails.notificationType}</SpH5>
                          : <>&#8211;</>}
                      </Col>
                    </Row>
                  </SpForm>
                  <SpForm>
                    <Row type="flex" justify="start" align="middle">
                      <Col span={10}>
                        <span>Count of Billing Cycles</span>
                      </Col>
                      <Col span={12}>
                        {plansDetails.plan2TotalPayments
                          ? <SpH5>{plansDetails.plan2TotalPayments}</SpH5>
                          : <>&#8211;</>}
                      </Col>
                    </Row>
                  </SpForm>
                  <SpForm>
                    <Row type="flex" justify="start" align="middle">
                      <Col span={10}>
                        <span>Customer Pause or Cancel</span>
                      </Col>
                      <Col span={12}>
                        <SpH5>{plansDetails.canCustomerCancelOrPause ? 'YES' : 'NO'}</SpH5>
                      </Col>
                    </Row>
                  </SpForm>
                  <SpForm>
                    <Row type="flex" justify="start" align="middle">
                      <Col span={10}>
                        <span>Resume Method Enabled for Customer</span>
                      </Col>
                      <Col span={10}>
                        {plansDetails.subscriptionChargeType
                          ? <SpH5>{plansDetails.subscriptionChargeType}</SpH5>
                          : <>&#8211;</>}
                      </Col>
                    </Row>
                  </SpForm>
                </Col>
                <Col span={10}>
                  <div className="mt-2 mb-1">
                    <label htmlFor="">
                      <SpH5>Plan Items</SpH5>
                    </label>
                  </div>
                  <div
                    className="mb-4"
                    style={{
                      border: '1px solid #C4C4C4',
                      background: '#ffffff',
                      borderRadius: '6px',
                      paddingLeft: '5px',
                    }}
                  >
                    <Row type="flex" className="my-2 mb-3 mt-2">
                      <Col span={6}>
                        <div className="pt-1 pb-1 pl-1"><SpText fontWeight="600">Name</SpText></div>
                      </Col>
                      <Col span={18}>
                        <Row type="flex" justify="end">
                          <Col span={5} className="pt-1 mr-3" align="left">
                            <SpText fontWeight="600" fontSize="14px">Recurring?</SpText>
                          </Col>
                          <Col span={4} className="pt-1 mr-3" align="left">
                            <SpText fontWeight="600" fontSize="14px">Rate</SpText>
                          </Col>
                          <Col span={4} className="pt-1 mr-3" align="center">
                            <SpText fontWeight="600" fontSize="14px">Quantity</SpText>
                          </Col>
                          <Col span={4} className="pt-1 mr-3" align="right">
                            <SpText fontWeight="600" fontSize="14px">Total</SpText>
                          </Col>
                          <Col span={1} className="pt-1" align="right" />
                        </Row>
                      </Col>
                    </Row>
                    <>
                      <Row type="flex" className="my-2 mb-3 mt-2" style={{ padding: '0 0 0 5px' }}>
                        {plansDetails?.plan2LineItems?.map((item) => (
                          <>
                            <Col span={6} className="pb-1">
                              <SpH5>{item?.name}</SpH5>
                            </Col>
                            <Col span={18}>
                              <Row type="flex" justify="end">
                                <Col span={3} className="pt-1 mr-2">
                                  <SpH5>{(item?.item?.recurring === true) ? 'Yes' : 'No'}</SpH5>
                                </Col>
                                <Col span={5} className="pt-1 mr-4">
                                  <SpH5>{prefix} {formatNumber((item?.item?.price / 100).toFixed(2), currencyType)}</SpH5>
                                </Col>
                                <Col span={4} className="mr-3">
                                  <SpH5>{item?.quantity}</SpH5>
                                </Col>
                                <Col span={4} className="pt-1 mr-3" align="right">
                                  <SpText fontWeight="600">{prefix} {formatNumber((item?.quantity * (item?.item?.price / 100)).toFixed(2), currencyType)}</SpText>
                                </Col>
                              </Row>
                            </Col>
                          </>
                        ))}
                      </Row>
                    </>
                    <hr />
                    <Row type="flex" justify="end" className="mr-3 mb-1">
                      <Col className="mt-1 pb-1 mr-3 pr-1">
                        <SpText className="mr-5" fontWeight="500" fontSize="20px">
                          Total Amount
                        </SpText>
                        <SpText fontWeight="600" fontSize="20px">
                          {prefix} {formatNumber((plansDetails?.amount / 100).toFixed(2), currencyType)}
                        </SpText>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
              <SpForm>
                <Row type="flex" justify="start" align="middle">
                  <Col span={3}>
                    <span>Internal Note</span>
                  </Col>
                  <Col span={6}>
                    {plansDetails.note
                      ? <SpH5>{plansDetails.note}</SpH5>
                      : <>&#8211;</>}
                  </Col>
                </Row>
              </SpForm>
              <SpForm>
                <Row type="flex" justify="start" align="middle">
                  <Col span={3}>
                    <span>Delay Start Date</span>
                  </Col>
                  <Col span={6}>
                    {plansDetails.delayStartDays
                      ? <SpH5>{plansDetails.delayStartDays}</SpH5>
                      : <>&#8211;</>}
                  </Col>
                </Row>
              </SpForm>
              <SpForm>
                <Row type="flex" justify="start" align="middle">
                  <Col span={3}>
                    <span>Free Trial Days</span>
                  </Col>
                  <Col span={6}>
                    {plansDetails.freeTrialDays
                      ? <SpH5>{plansDetails.freeTrialDays}</SpH5>
                      : <>&#8211;</>}
                  </Col>
                </Row>
              </SpForm>
              <SpForm>
                <Row type="flex" justify="start" align="middle">
                  <Col span={3}>
                    <span>Overdue Fees</span>
                  </Col>
                  <Col span={6}>
                    {plansDetails.description
                      ? <SpH5>{plansDetails.overDueFees / 100}{plansDetails?.overDueFeeType === 'PERCENT' ? '%' : prefix}</SpH5>
                      : <>&#8211;</>}
                  </Col>
                </Row>
              </SpForm>
              <SpForm>
                <Row type="flex" justify="start" align="middle">
                  <Col span={3}>
                    <span>Saved Payment Methods Limit</span>
                  </Col>
                  <Col span={6}>
                    {plansDetails.savedPaymentsLimit
                      ? <SpH5>{plansDetails.savedPaymentsLimit}</SpH5>
                      : <>&#8211;</>}
                  </Col>
                </Row>
              </SpForm>
              <SpForm>
                <Row type="flex" justify="start" align="middle">
                  <Col span={3}>
                    <span>Allow Manual Payments</span>
                  </Col>
                  <Col span={6}>
                    <SpH5>{plansDetails?.allowManualPayments ? 'YES' : 'NO'}</SpH5>
                  </Col>
                </Row>
              </SpForm>
              <SpForm>
                <Row type="flex" justify="start" align="middle">
                  <Col span={3}>
                    <span>Days To Next Charge Attempt After Fail</span>
                  </Col>
                  <Col span={6}>
                    {plansDetails.nextChargeAttemptDelayDays
                      ? <SpH5>{plansDetails.nextChargeAttemptDelayDays}</SpH5>
                      : <>&#8211;</>}
                  </Col>
                </Row>
              </SpForm>
              <SpForm>
                <Row type="flex" justify="start" align="middle">
                  <Col span={3}>
                    <span>Notify</span>
                  </Col>
                  <Col span={6}>
                    {plansDetails.notificationType
                      ? <SpH5>{plansDetails.notificationType === 'ALL' ? 'EMAIL and SMS' : plansDetails.notificationType}</SpH5>
                      : <>&#8211;</>}
                  </Col>
                </Row>
              </SpForm>
              <SpForm>
                <Row type="flex" justify="start" align="middle">
                  <Col span={3}>
                    <span>Count of Billing Cycles</span>
                  </Col>
                  <Col span={6}>
                    {plansDetails.plan2TotalPayments
                      ? <SpH5>{plansDetails.plan2TotalPayments}</SpH5>
                      : <>&#8211;</>}
                  </Col>
                </Row>
              </SpForm>
              <SpForm>
                <Row type="flex" justify="start" align="middle">
                  <Col span={3}>
                    <span>Customer Pause or Cancel</span>
                  </Col>
                  <Col span={6}>
                    <SpH5>{plansDetails.canCustomerCancelOrPause ? 'YES' : 'NO'}</SpH5>
                  </Col>
                </Row>
              </SpForm>
              <SpForm>
                <Row type="flex" justify="start" align="middle">
                  <Col span={3}>
                    <span>Resume Method Enabled for Customer</span>
                  </Col>
                  <Col span={6}>
                    {plansDetails.subscriptionChargeType
                      ? <SpH5>{plansDetails.subscriptionChargeType}</SpH5>
                      : <>&#8211;</>}
                  </Col>
                </Row>
              </SpForm>
              <SpForm>
                <Row type="flex" justify="start">
                  <Col span={12}>
                    <Col span={11}>
                      <span>In Plan Manager</span>
                    </Col>
                    <Col span={6}>
                      <SpH5>{plansDetails.addToPlanManager ? 'YES' : 'NO'}</SpH5>
                    </Col>
                  </Col>
                  <Col span={12} className="d-flex justify-content-end">
                    <SpH5 color="#7f7f7f">{plansDetails.gid}</SpH5>
                  </Col>
                </Row>
              </SpForm>
            </Card>
            <Card className="pl-2">
              <Row type="flex" justify="space-between" className="my-2">
                <Col>
                  <SpText className="text-uppercase mb-3" fontSize="20px">SUBSCRIPTIONS WITH THIS PLAN</SpText>
                </Col>
              </Row>
              <Table
                onRow={(record) => ({
                  onClick: () => {
                    selectedRecord(record);
                  },
                })}
                rowClassName={() => 'ant-table-clickable-row'}
                columns={columns}
                rowKey={(record) => record.gid}
                dataSource={subscription}
                pagination={pagination}
                onChange={handleTableChange}
              />
            </Card>
          </>
        )
      }
    </>
  );
};

const mapStateToProps = (state) => ({
  plansDetails: state.planv2Details,
  plansDetailsStatus: state.planv2Details.plansDetailsStatus,
  subscription: state.planv2Details.subscription.content,
  totalElements: state.planv2Details.subscription ? state.planv2Details.subscription.totalElements : '',
  loading: state.loading.loading,
  test: state.account.test,
  isSuperUser: state.user.superUser,
  selectedAccount: state.account.selectedAccount,
  submitting: state.loading.submitting,
  validator: state.loading.validator,
});

const mapDispatchToProps = (dispatch) => ({
  fetchPlanV2Details: gid => dispatch(planv2DetailsActions.fetchPlanV2Details({
    payload: gid,
  })),
  fetchPlanV2Subscription: param => dispatch(planv2DetailsActions.fetchPlanV2Subscription({
    payload: param,
  })),
  setNotification: ({ type, payload }) => dispatch(notificationActions.setNotification({
    type,
    payload,
  })),
});

// $FlowFixMe
export default connect(mapStateToProps, mapDispatchToProps)(PlanDetails);
